import React, {
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";

/* ======================================================================
   CONFIG
   ====================================================================== */
const BATTERS_SOURCE =
  import.meta.env?.VITE_MLB_BATTERS_URL ||
  "/data/mlb/latest/batter_projections.json";

const PITCHERS_SOURCE =
  import.meta.env?.VITE_MLB_PITCHERS_URL ||
  "/data/mlb/latest/pitcher_projections.json";

const SITE_ID_SOURCE =
  import.meta.env?.VITE_MLB_SITE_ID_URL ||
  "/data/mlb/latest/site_ids.json";

/* ✅ separate point distributions JSONs */
const BATTER_POINT_DISTS =
  import.meta.env?.VITE_MLB_BATTER_POINT_DISTS_URL ||
  "/data/mlb/latest/batter_point_distributions.json";

const PITCHER_POINT_DISTS =
  import.meta.env?.VITE_MLB_PITCHER_POINT_DISTS_URL ||
  "/data/mlb/latest/pitcher_point_distributions.json";

const SITES = {
  dk: { key: "dk", label: "DK", logo: "/logos/dk.png" },
  fd: { key: "fd", label: "FD", logo: "/logos/fd.png" },
  both: { key: "both", label: "Both" },
};

/* ======================================================================
   ALIASES (sheet headers -> canonical keys)
   ====================================================================== */
const ALIASES = {
  player: ["player", "name", "pitcher", "player name"],
  team: ["team", "teamabbrev", "tm"],
  opp: ["opp", "opponent"],

  dk_pos: ["dk pos", "dk position", "dkpos", "pos", "position"],
  fd_pos: ["fd pos", "fd position", "fdpos"],

  dk_sal: ["dk sal", "dk salary", "draftkings salary", "dk$"],
  dk_proj: ["dk proj", "dk projection", "dk pts", "draftkings proj"],
  dk_val: ["dk val", "dk value"],
  dk_pown: ["dk pown%", "dk pown", "dk own%", "dk ownership%", "dk_pown"],
  dk_floor: ["dk floor", "dk_floor"],
  dk_ceiling: ["dk ceiling", "dk_ceiling"],
  dk_rtg: ["dk rtg", "dk rating", "dk_rating"],
  dk_optimal: ["dk optimal", "dk opt", "dk_optimal", "dk optimal%", "dk opt%"],

  fd_sal: ["fd sal", "fd salary", "fanduel salary", "fd$"],
  fd_proj: ["fd proj", "fd projection", "fd pts", "fanduel proj"],
  fd_val: ["fd val", "fd value"],
  fd_pown: ["fd pown%", "fd pown", "fd own%", "fd ownership%", "fd_pown"],
  fd_floor: ["fd floor", "fd_floor"],
  fd_ceiling: ["fd ceiling", "fd_ceiling"],
  fd_rtg: ["fd rtg", "fd rating", "fd_rating"],
  fd_optimal: ["fd optimal", "fd opt", "fd_optimal", "fd optimal%", "fd opt%"],
};

/* ======================================================================
   HELPERS
   ====================================================================== */
const lc = (s) => String(s ?? "").toLowerCase().trim();
const buildLowerMap = (row) => {
  const low = {};
  for (const [k, v] of Object.entries(row)) low[lc(k)] = v;
  return low;
};
const firstKey = (obj, names) => names.map(lc).find((n) => n in obj);
const pick = (obj, names) => obj[firstKey(obj, names) ?? ""];

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[,$%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const pctNum = (v) => {
  const n = num(v);
  if (n === null) return null;
  return Math.max(0, Math.min(100, n));
};

const fmtMoney0 = (v) => (num(v) === null ? "" : `$${num(v).toLocaleString()}`);
const fmt1 = (v) => (num(v) === null ? "" : num(v).toFixed(1));
const fmtPct1 = (v) =>
  v === null || v === undefined || v === "" ? "" : `${pctNum(v).toFixed(1)}%`;

const teamLogo = (team) =>
  `/logos/mlb/${String(team || "").toUpperCase()}.png`;

const keynorm = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");

function getPosForSite(row, site) {
  if (!row) return "";

  const normalizeParts = (raw) =>
    String(raw || "")
      .toUpperCase()
      .split(/[\/,;|\s]+/g)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => {
        if (p === "LF" || p === "CF" || p === "RF") return "OF";
        if (p === "SP" || p === "RP") return "P";
        return p;
      });

  if (site === "dk") return normalizeParts(row.dk_pos || row.pos || "").join("/");
  if (site === "fd") return normalizeParts(row.fd_pos || row.pos || "").join("/");

  const dkParts = normalizeParts(row.dk_pos || "");
  const fdParts = normalizeParts(row.fd_pos || "");
  const fallbackParts = normalizeParts(row.pos || "");
  const merged = [...new Set([...(dkParts.length ? dkParts : []), ...(fdParts.length ? fdParts : [])])];

  if (merged.length) return merged.join("/");
  return fallbackParts.join("/");
}

/* ======================================================================
   SITE ID / SLATE HELPERS
   ====================================================================== */
function buildSiteIdIndex(raw) {
  const out = {
    dk: new Map(),
    fd: new Map(),
  };

  if (!raw || typeof raw !== "object") return out;

  for (const siteKey of ["dk", "fd"]) {
    const arr = Array.isArray(raw?.[siteKey]) ? raw[siteKey] : [];
    for (const row of arr) {
      const nameKey = keynorm(row?.name);
      const teamKey = String(row?.team || "").toUpperCase().trim();
      const comboKey = `${nameKey}__${teamKey}`;

      out[siteKey].set(comboKey, {
        id: row?.id ?? null,
        salary: num(row?.salary),
        early_id: row?.early_id ?? null,
        early_salary: num(row?.early_salary),
        late_id: row?.late_id ?? null,
        late_salary: num(row?.late_salary),
        pos: row?.pos ?? null,
        pos_primary: row?.pos_primary ?? null,
        team: row?.team ?? null,
        opp: row?.opp ?? null,
        name: row?.name ?? null,
      });
    }
  }

  return out;
}

function getSiteIdEntry(siteIdIndex, row, site) {
  const siteKey = site === "fd" ? "fd" : "dk";
  const nameKey = keynorm(row?.player);
  const teamKey = String(row?.team || "").toUpperCase().trim();
  const comboKey = `${nameKey}__${teamKey}`;
  return siteIdIndex?.[siteKey]?.get(comboKey) ?? null;
}

function getSlateSalary(entry, slate) {
  if (!entry) return null;
  if (slate === "early") return num(entry.early_salary);
  if (slate === "late") return num(entry.late_salary);
  return num(entry.salary);
}

function hasSlateEntry(entry, slate) {
  if (!entry) return false;

  if (slate === "early") {
    return !!entry.early_id && num(entry.early_salary) !== null;
  }

  if (slate === "late") {
    return !!entry.late_id && num(entry.late_salary) !== null;
  }

  if (slate === "main") {
    const sal = num(entry.salary);
    return !!entry.id && sal !== null && sal > 0 && sal < 50000;
  }

  if (slate === "all") {
    return (
      (!!entry.id && num(entry.salary) !== null && num(entry.salary) > 0) ||
      (!!entry.early_id && num(entry.early_salary) !== null && num(entry.early_salary) > 0) ||
      (!!entry.late_id && num(entry.late_salary) !== null && num(entry.late_salary) > 0)
    );
  }

  return true;
}

function rowMatchesSlate(siteIdIndex, row, site, slate) {
  if (slate === "all") return true;

  if (site === "dk" || site === "fd") {
    const entry = getSiteIdEntry(siteIdIndex, row, site);
    return hasSlateEntry(entry, slate);
  }

  // BOTH => keep row if it exists on either site for that slate
  const dkEntry = getSiteIdEntry(siteIdIndex, row, "dk");
  const fdEntry = getSiteIdEntry(siteIdIndex, row, "fd");
  return hasSlateEntry(dkEntry, slate) || hasSlateEntry(fdEntry, slate);
}

function getRenderedSalary(siteIdIndex, row, site, slate, baseValue) {
  if (slate === "all") return num(baseValue);

  if (site !== "dk" && site !== "fd") return num(baseValue);

  const entry = getSiteIdEntry(siteIdIndex, row, site);
  return getSlateSalary(entry, slate);
}

/* ======================================================================
   NORMALIZERS
   ====================================================================== */
function normalizeCommon(row) {
  const low = buildLowerMap(row);

  const dkPosRaw = (pick(low, ALIASES.dk_pos) || "").toUpperCase();
  const fdPosRaw = (pick(low, ALIASES.fd_pos) || "").toUpperCase();
  const genericPos = (dkPosRaw || fdPosRaw || "").toUpperCase();

  return {
    player: pick(low, ALIASES.player),
    dk_pos: dkPosRaw,
    fd_pos: fdPosRaw,
    pos: genericPos,
    team: (pick(low, ALIASES.team) || "").toUpperCase(),
    opp: (pick(low, ALIASES.opp) || "").toUpperCase(),

    dk_sal: num(pick(low, ALIASES.dk_sal)),
    fd_sal: num(pick(low, ALIASES.fd_sal)),

    dk_proj: num(pick(low, ALIASES.dk_proj)),
    dk_val: num(pick(low, ALIASES.dk_val)),
    dk_pown: pctNum(pick(low, ALIASES.dk_pown)),
    dk_floor: num(pick(low, ALIASES.dk_floor)),
    dk_ceiling: num(pick(low, ALIASES.dk_ceiling)),
    dk_rtg: num(pick(low, ALIASES.dk_rtg)),
    dk_optimal: pctNum(pick(low, ALIASES.dk_optimal)),

    fd_proj: num(pick(low, ALIASES.fd_proj)),
    fd_val: num(pick(low, ALIASES.fd_val)),
    fd_pown: pctNum(pick(low, ALIASES.fd_pown)),
    fd_floor: num(pick(low, ALIASES.fd_floor)),
    fd_ceiling: num(pick(low, ALIASES.fd_ceiling)),
    fd_rtg: num(pick(low, ALIASES.fd_rtg)),
    fd_optimal: pctNum(pick(low, ALIASES.fd_optimal)),
  };
}

function normalizeBatter(row) {
  return { type: "BAT", ...normalizeCommon(row) };
}

function normalizePitcher(row) {
  const base = normalizeCommon(row);

  return {
    type: "P",
    ...base,
    dk_pos: (base.dk_pos || "P").toUpperCase(),
    fd_pos: (base.fd_pos || "P").toUpperCase(),
    pos: "P",
  };
}

/* ======================================================================
   FETCH JSON
   ====================================================================== */
async function fetchJsonGuard(url) {
  const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const ct = (r.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    const txt = await r.text();
    throw new Error(`Expected JSON, got ${ct || "unknown"}: ${txt.slice(0, 30)}…`);
  }
  const j = await r.json();
  return Array.isArray(j) ? j : j?.rows || j?.data || j?.items || [];
}

function useMlbCombined(battersUrl, pitchersUrl) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErr("");

      try {
        const [batRaw, pitRaw] = await Promise.all([
          fetchJsonGuard(battersUrl),
          fetchJsonGuard(pitchersUrl),
        ]);

        const bats = (batRaw || []).map(normalizeBatter).filter((r) => r.player);
        const pits = (pitRaw || []).map(normalizePitcher).filter((r) => r.player);

        if (alive) setRows([...bats, ...pits]);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [battersUrl, pitchersUrl]);

  return { rows, loading, err };
}

function useAnyJson(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setData(j);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [url]);

  return { data, loading, err };
}

/* ======================================================================
   LAST UPDATED
   ====================================================================== */
function toMetaUrl(urlLike) {
  return String(urlLike || "").replace(/[^/]+$/, "meta.json");
}
function parseMetaToDate(meta) {
  const iso =
    meta?.updated_iso ||
    meta?.updated_utc ||
    meta?.source_mtime_iso ||
    meta?.last_updated ||
    meta?.timestamp;

  if (iso) {
    const d = new Date(iso);
    if (!isNaN(d)) return d;
  }

  const epoch = meta?.updated_epoch ?? meta?.epoch;
  if (Number.isFinite(epoch)) {
    const d = new Date(epoch * (epoch > 10_000_000_000 ? 1 : 1000));
    if (!isNaN(d)) return d;
  }

  return null;
}

function useLastUpdatedFromSources(a, b) {
  const [date, setDate] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        for (const u of [a, b].filter(Boolean)) {
          const metaUrl = toMetaUrl(u);
          const r = await fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" });
          if (!r.ok) continue;
          const meta = await r.json();
          const d = parseMetaToDate(meta);
          if (d && alive) {
            setDate(d);
            return;
          }
        }
      } catch (_) {}
    })();

    return () => {
      alive = false;
    };
  }, [a, b]);

  return date
    ? date.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;
}

/* ======================================================================
   CSV EXPORT
   ====================================================================== */
const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};

function downloadCSV(rows, cols, site, slate, siteIdIndex, fname = "mlb_projections.csv") {
  const header = cols.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      cols
        .map((c) => {
          const k = c.key;

          if (k === "pos") return escapeCSV(getPosForSite(r, site));

          let val = r[k];

          if (k === "dk_sal") {
            val = getRenderedSalary(siteIdIndex, r, "dk", slate, r.dk_sal);
            val = fmtMoney0(val);
          } else if (k === "fd_sal") {
            val = getRenderedSalary(siteIdIndex, r, "fd", slate, r.fd_sal);
            val = fmtMoney0(val);
          } else if (
            k === "dk_pown" ||
            k === "fd_pown" ||
            k === "dk_optimal" ||
            k === "fd_optimal"
          ) {
            val = fmtPct1(val);
          } else if (typeof val === "number") {
            val = fmt1(val);
          }

          return escapeCSV(val ?? "");
        })
        .join(",")
    )
    .join("\n");

  const blob = new Blob([header + "\n" + body], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}

/* ======================================================================
   TEAMS DROPDOWN
   ====================================================================== */
function TeamsDropdown({ allTeams, selected, onChange, isDark }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0, width: 0 });

  const allSet = useMemo(() => new Set(selected || []), [selected]);

  const recalc = () => {
    const el = btnRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();
    const panelWidth = Math.max(320, Math.round(r.width));
    const padding = 12;

    let left = Math.round(r.right - panelWidth);
    const top = Math.round(r.bottom + 8);

    if (left + panelWidth > window.innerWidth - padding) {
      left = window.innerWidth - panelWidth - padding;
    }
    if (left < padding) left = padding;

    setPos({ left, top, width: panelWidth });
  };

  useLayoutEffect(() => {
    if (!open) return;
    recalc();
    const onResizeOrScroll = () => recalc();
    window.addEventListener("resize", onResizeOrScroll);
    window.addEventListener("scroll", onResizeOrScroll, true);
    return () => {
      window.removeEventListener("resize", onResizeOrScroll);
      window.removeEventListener("scroll", onResizeOrScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (tm) => {
    const next = new Set(allSet);
    next.has(tm) ? next.delete(tm) : next.add(tm);
    onChange(Array.from(next));
  };

  const summaryClass =
    "px-3 py-1.5 rounded-lg text-sm font-medium shadow border whitespace-nowrap " +
    (isDark
      ? "bg-slate-950 text-slate-100 border-slate-700 hover:bg-slate-900"
      : "bg-white text-gray-800 border-gray-200 hover:bg-gray-50");

  const panelClass =
    "rounded-xl shadow-lg p-2 border " +
    (isDark
      ? "bg-slate-950 border-slate-700 text-slate-100"
      : "bg-white border-gray-200 text-gray-900");

  const buttonBase =
    "px-2 py-1 rounded-md text-xs border " +
    (isDark
      ? "bg-slate-900 border-slate-700 hover:bg-slate-800"
      : "bg-gray-100 border-gray-200 hover:bg-gray-200");

  const Panel = (
    <div
      ref={panelRef}
      className={panelClass}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        width: pos.width,
        zIndex: 9999,
      }}
    >
      <div className="flex items-center justify-between mb-2 text-xs">
        <button type="button" className={buttonBase} onClick={() => onChange([...allTeams])}>
          Select all
        </button>
        <button type="button" className={buttonBase} onClick={() => onChange([])}>
          Clear all
        </button>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-64 overflow-auto pr-1">
        {allTeams.map((t) => (
          <label
            key={t}
            className={
              "flex items-center gap-2 text-xs select-none " +
              (isDark ? "text-slate-100" : "text-gray-800")
            }
          >
            <input
              type="checkbox"
              className="accent-indigo-600"
              checked={allSet.has(t)}
              onChange={() => toggle(t)}
            />
            <span>{t}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        className={summaryClass}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Filter teams"
      >
        Teams ({selected.length}/{allTeams.length})
      </button>

      {open ? createPortal(Panel, document.body) : null}
    </div>
  );
}

/* ======================================================================
   CONDITIONAL FORMATTING
   ====================================================================== */
const HIGHER_IS_BETTER = new Set([
  "dk_proj",
  "dk_val",
  "dk_floor",
  "dk_ceiling",
  "dk_rtg",
  "dk_optimal",
  "fd_proj",
  "fd_val",
  "fd_floor",
  "fd_ceiling",
  "fd_rtg",
  "fd_optimal",
]);
const LOWER_IS_BETTER = new Set(["dk_pown", "fd_pown"]);

function computeStats(rows, cols) {
  const stats = {};
  for (const c of cols) {
    const key = c.key;
    if (key === "pos") continue;

    let min = Infinity;
    let max = -Infinity;
    let any = false;

    for (const r of rows) {
      let v = r[key];
      if (v === null || v === undefined || isNaN(Number(v))) continue;
      const n = Number(v);
      if (!isFinite(n)) continue;
      any = true;
      if (n < min) min = n;
      if (n > max) max = n;
    }

    if (any && min !== max) stats[key] = { min, max };
  }
  return stats;
}

function colorFor(value, key, stats, isDark = false) {
  if (value == null || isNaN(Number(value))) return null;
  const st = stats[key];
  if (!st) return null;

  const dir = HIGHER_IS_BETTER.has(key)
    ? "higher"
    : LOWER_IS_BETTER.has(key)
    ? "lower"
    : null;
  if (!dir) return null;

  let t = (Number(value) - st.min) / (st.max - st.min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;

  let h, s, l;

  if (isDark) {
    if (t < 0.5) {
      const u = t / 0.5;
      h = 8 + u * 42;
      s = 90;
      l = 70 - u * 8;
    } else {
      const u = (t - 0.5) / 0.5;
      h = 50 + u * 70;
      s = 88;
      l = 62 + u * 8;
    }
  } else {
    if (t < 0.5) {
      const u = t / 0.5;
      h = 8 + u * 42;
      s = 85;
      l = 48 - u * 4;
    } else {
      const u = (t - 0.5) / 0.5;
      h = 50 + u * 70;
      s = 80;
      l = 44 - u * 4;
    }
  }

  return `hsl(${h} ${s}% ${l}%)`;
}

/* ======================================================================
   POINT DISTS
   ====================================================================== */
function collapseBinsForBatters(bins) {
  if (!Array.isArray(bins)) return [];
  const parsed = bins
    .map((b) => ({
      bin_start: Number(b?.bin_start),
      bin_end: Number(b?.bin_end),
      prob: Number(b?.prob),
    }))
    .filter((b) => Number.isFinite(b.bin_start) && Number.isFinite(b.bin_end));

  parsed.sort((a, z) => a.bin_start - z.bin_start);

  let tailProb = 0;
  const head = [];

  for (const b of parsed) {
    const p = Number.isFinite(b.prob) ? b.prob : 0;
    if (b.bin_start >= 20) tailProb += p;
    else head.push({ ...b, prob: p });
  }

  if (tailProb > 0) {
    head.push({
      bin_start: 20,
      bin_end: 9999,
      prob: tailProb,
      _label: "20+",
    });
  }

  return head;
}

function PointDistModal({
  open,
  onClose,
  isDark,
  site,
  playerName,
  bins,
  proj,
  isBatter,
}) {
  if (!open) return null;

  const finalBins = isBatter
    ? collapseBinsForBatters(bins)
    : Array.isArray(bins)
    ? bins
    : [];

const chartData = finalBins.map((b, idx) => {
  const a = Number(b?.bin_start);
  const z = Number(b?.bin_end);
  const p = Number(b?.prob);

  const label =
    b?._label ||
    (Number.isFinite(a) && Number.isFinite(z) ? `${a}-${z}` : `bin-${idx + 1}`);

  const mid =
    label === "20+"
      ? 20
      : Number.isFinite(a) && Number.isFinite(z)
      ? (a + z) / 2
      : idx;

  return {
    label,
    shortLabel: label === "20+" ? "20+" : Number.isFinite(a) ? `${a}` : label,
    bin_start: a,
    bin_end: z,
    mid,
    probPct: Number.isFinite(p) ? p * 100 : 0,
  };
});

  const hasData = chartData.length > 0;

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const media = window.matchMedia("(max-width: 639px)");
    const syncMobile = () => setIsMobile(media.matches);

    syncMobile();
    media.addEventListener?.("change", syncMobile);

    return () => media.removeEventListener?.("change", syncMobile);
  }, []);

  const projX = (() => {
    if (!Number.isFinite(proj)) return null;

    if (isBatter && proj >= 20) {
      const twenty = chartData.find((d) => d.label === "20+");
      return twenty?.mid ?? 20;
    }

    return proj;
  })();

  const xTicks = useMemo(() => {
  if (!chartData.length) return [];

  if (isBatter) {
    return chartData.map((d) => d.mid).filter(Number.isFinite);
  }

  return chartData
    .filter((_, i) => (isMobile ? i % 4 === 0 : i % 2 === 0))
    .map((d) => d.mid)
    .filter(Number.isFinite);
}, [chartData, isBatter, isMobile]);

  const xDomain = useMemo(() => {
    const mids = chartData
      .map((d) => d.mid)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    if (!mids.length) return [0, 1];
    if (mids.length === 1) return [mids[0] - 2.5, mids[0] + 2.5];

    const firstGap = Math.abs(mids[1] - mids[0]) || 5;
    const lastGap = Math.abs(mids[mids.length - 1] - mids[mids.length - 2]) || firstGap;

    return [
      mids[0] - firstGap / 2,
      mids[mids.length - 1] + lastGap / 2,
    ];
  }, [chartData]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        className={[
          "relative w-full max-w-3xl rounded-2xl border shadow-2xl overflow-hidden",
          isDark
            ? "border-slate-700/60 bg-slate-950 text-slate-100"
            : "border-slate-200 bg-white text-slate-900",
        ].join(" ")}
      >
        <div className={isDark ? "bg-slate-950/95" : "bg-white"}>
          <div className="flex items-start justify-between gap-4 px-5 pt-5">
            <div className="min-w-0">
              <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>
                Fantasy Points Distribution
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-2">
                <div className="text-lg font-extrabold tracking-tight truncate">
                  {playerName || "—"}
                </div>

                <span
                  className={[
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide",
                    site === "DK"
                      ? isDark
                        ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                        : "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      : isDark
                      ? "bg-sky-500/15 text-sky-200 border border-sky-500/30"
                      : "bg-sky-50 text-sky-800 border border-sky-200",
                  ].join(" ")}
                >
                  {site}
                </span>

                {Number.isFinite(proj) ? (
                  <span className={isDark ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                    Proj: <span className="font-semibold tabular-nums">{proj.toFixed(1)}</span>
                  </span>
                ) : null}
              </div>
            </div>

            <button
              className={[
                "shrink-0 rounded-xl border px-3 py-2 text-sm font-semibold",
                isDark
                  ? "border-slate-700 bg-slate-900/40 hover:bg-slate-900"
                  : "border-slate-200 bg-slate-50 hover:bg-slate-100",
              ].join(" ")}
              onClick={onClose}
            >
              Close
            </button>
          </div>

          <div className="px-3 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pt-4">
            {!hasData ? (
              <div className={isDark ? "text-sm text-slate-300" : "text-sm text-slate-700"}>
                No distribution data found for this player yet.
              </div>
            ) : (
              <div className={isMobile ? "h-[360px] w-full" : "h-[320px] w-full"}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 10, right: 18, left: 8, bottom: isMobile ? 42 : 18 }}
                    barCategoryGap={isMobile ? "12%" : "8%"}
                  >
                    <CartesianGrid
                      stroke={isDark ? "#334155" : "#e5e7eb"}
                      strokeDasharray="3 3"
                    />
                      <XAxis
                        type="number"
                        dataKey="mid"
                        domain={xDomain}
                        ticks={xTicks}
                        tickFormatter={(v) => {
                          const match = chartData.find((d) => d.mid === v);
                          if (match?.label === "20+") return "20+";
                          return `${Math.round(v)}`;
                        }}
                        tick={{ fontSize: isMobile ? 11 : 12, fill: isDark ? "#cbd5f5" : "#334155" }}
                        axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                        tickLine={false}
                        angle={isMobile ? -35 : 0}
                        textAnchor={isMobile ? "end" : "middle"}
                        height={isMobile ? 52 : 30}
                      />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 11, fill: isDark ? "#cbd5f5" : "#334155" }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(56,189,248,0.12)" }}
                      contentStyle={{
                        backgroundColor: isDark ? "#020617" : "#ffffff",
                        border: "1px solid #38bdf8",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      labelStyle={{ color: isDark ? "#e2e8f0" : "#0f172a" }}
                      formatter={(v) => [`${Number(v).toFixed(2)}%`, "Probability"]}
                      labelFormatter={(_, payload) => {
                      const p = payload?.[0]?.payload;
                      return p?.label ? `Bin ${p.label}` : "";
                    }}
                    />

                    {Number.isFinite(projX) ? (
                      <ReferenceLine
                        x={projX}
                        stroke="#38bdf8"
                        strokeWidth={2}
                        strokeDasharray="6 4"
                      />
                    ) : null}

                    <Bar
                      dataKey="probPct"
                      radius={[6, 6, 0, 0]}
                      fill="#7dd3fc"
                      stroke="#38bdf8"
                      strokeWidth={1}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {isBatter ? (
              <div className={isDark ? "mt-3 text-xs text-slate-400" : "mt-3 text-xs text-slate-500"}>
                Batter bins are capped to <span className="font-semibold">20+</span> for readability.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ======================================================================
   PAGE
   ====================================================================== */
export default function MlbProjections() {
  const { rows, loading, err } = useMlbCombined(BATTERS_SOURCE, PITCHERS_SOURCE);

  const { data: siteIdRaw } = useAnyJson(SITE_ID_SOURCE);
  const { data: batterPointDistsRaw } = useAnyJson(BATTER_POINT_DISTS);
  const { data: pitcherPointDistsRaw } = useAnyJson(PITCHER_POINT_DISTS);

  const [site, setSite] = useState("both");
  const [slate, setSlate] = useState("all");
  const [kind, setKind] = useState("both");
  const [theme, setTheme] = useState("dark");
  const [q, setQ] = useState("");

  const isDark = theme === "dark";

  const [pointDistOpen, setPointDistOpen] = useState(false);
  const [pointDistSite, setPointDistSite] = useState("DK");
  const [pointDistPlayerKey, setPointDistPlayerKey] = useState("");
  const [pointDistPlayerName, setPointDistPlayerName] = useState("");
  const [pointDistIsBatter, setPointDistIsBatter] = useState(false);

  const siteIdIndex = useMemo(() => buildSiteIdIndex(siteIdRaw), [siteIdRaw]);

  const allTeams = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => String(r.team || "").toUpperCase()).filter(Boolean))
      ).sort(),
    [rows]
  );

  const [teamsSel, setTeamsSel] = useState(allTeams);
  useEffect(() => {
    if (allTeams.length && (!teamsSel || teamsSel.length === 0)) setTeamsSel(allTeams);
  }, [allTeams]); // eslint-disable-line react-hooks/exhaustive-deps

  const POS_WHITELIST = useMemo(() => ["P", "C", "1B", "2B", "3B", "SS", "OF"], []);
  const posOptions = useMemo(() => POS_WHITELIST, [POS_WHITELIST]);

  const [posSel, setPosSel] = useState(posOptions);
  useEffect(() => {
    if (posOptions.length && (!posSel || posSel.length === 0)) setPosSel(posOptions);
  }, [posOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const rowHasAnyPos = (rowPos, selectedSet) => {
    const raw = String(rowPos || "").toUpperCase();
    if (!raw) return false;

    const parts = raw
      .split(/[\/,;|\s]+/g)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => p.replace(/[-–—].*$/, ""));

    const norm = parts.map((p) => {
      if (p === "LF" || p === "CF" || p === "RF") return "OF";
      if (p === "1" || p === "1B") return "1B";
      if (p === "2" || p === "2B") return "2B";
      if (p === "3" || p === "3B") return "3B";
      if (p === "SS") return "SS";
      if (p === "C") return "C";
      if (p === "P" || p === "SP" || p === "RP") return "P";
      return p;
    });

    return norm.some((p) => selectedSet.has(p));
  };

  const COLS_ORDER = useMemo(
    () => [
      { key: "type", label: "Type" },
      { key: "pos", label: "Pos" },
      { key: "player", label: "Player", sticky: true },
      { key: "team", label: "Team" },
      { key: "opp", label: "Opp" },

      { key: "dk_sal", label: "DK Sal", site: "dk" },
      { key: "fd_sal", label: "FD Sal", site: "fd" },

      { key: "dk_proj", label: "DK Proj", site: "dk" },
      { key: "dk_floor", label: "DK Floor", site: "dk" },
      { key: "dk_ceiling", label: "DK Ceil", site: "dk" },
      { key: "dk_pown", label: "DK pOWN%", site: "dk" },
      { key: "dk_optimal", label: "DK Opt%", site: "dk" },

      { key: "fd_proj", label: "FD Proj", site: "fd" },
      { key: "fd_floor", label: "FD Floor", site: "fd" },
      { key: "fd_ceiling", label: "FD Ceil", site: "fd" },
      { key: "fd_pown", label: "FD pOWN%", site: "fd" },
      { key: "fd_optimal", label: "FD Opt%", site: "fd" },

      { key: "dk_val", label: "DK Val", site: "dk" },
      { key: "fd_val", label: "FD Val", site: "fd" },
      { key: "dk_rtg", label: "DK Rtg", site: "dk" },
      { key: "fd_rtg", label: "FD Rtg", site: "fd" },
    ],
    []
  );

  const columns = useMemo(() => {
    return COLS_ORDER.filter((c) => {
      if (site !== "both" && c.site && c.site !== site) return false;

      if (
        ["dk_val", "fd_val", "dk_rtg", "fd_rtg", "dk_optimal", "fd_optimal"].includes(c.key)
      ) {
        return rows.some((r) => r[c.key] !== null && r[c.key] !== undefined);
      }

      return true;
    });
  }, [COLS_ORDER, rows, site]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const teamSet = new Set(teamsSel);
    const posSet = new Set(posSel);

    return rows.filter((r) => {
      if (kind !== "both") {
        if (kind === "bat" && r.type !== "BAT") return false;
        if (kind === "p" && r.type !== "P") return false;
      }

      if (teamSet.size && !teamSet.has(String(r.team || "").toUpperCase())) return false;

      const posForFilter = getPosForSite(r, site);
      if (posSet.size && !rowHasAnyPos(posForFilter, posSet)) return false;

      if (!rowMatchesSlate(siteIdIndex, r, site, slate)) return false;

      if (!needle) return true;

      const posForSearch = getPosForSite(r, site);
      return `${r.player ?? ""} ${r.team ?? ""} ${r.opp ?? ""} ${posForSearch ?? ""} ${r.type ?? ""}`
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, q, teamsSel, posSel, kind, site, slate, siteIdIndex]);

  const [sort, setSort] = useState({ key: "dk_proj", dir: "desc" });

  const sorted = useMemo(() => {
    const { key, dir } = sort;
    const sgn = dir === "asc" ? 1 : -1;
    const arr = [...filtered];

    arr.sort((a, b) => {
      if (key === "pos") {
        const av = getPosForSite(a, site);
        const bv = getPosForSite(b, site);
        return av.localeCompare(bv) * sgn;
      }

      if (key === "dk_sal") {
        const av = getRenderedSalary(siteIdIndex, a, "dk", slate, a.dk_sal);
        const bv = getRenderedSalary(siteIdIndex, b, "dk", slate, b.dk_sal);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * sgn;
      }

      if (key === "fd_sal") {
        const av = getRenderedSalary(siteIdIndex, a, "fd", slate, a.fd_sal);
        const bv = getRenderedSalary(siteIdIndex, b, "fd", slate, b.fd_sal);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * sgn;
      }

      const av = num(a[key]);
      const bv = num(b[key]);

      if (av === null && bv === null) {
        return String(a[key] ?? "").localeCompare(String(b[key] ?? "")) * sgn;
      }
      if (av === null) return 1;
      if (bv === null) return -1;
      return (av - bv) * sgn;
    });

    return arr;
  }, [filtered, sort, site, slate, siteIdIndex]);

  const onSort = (col) => {
    setSort((prev) =>
      prev.key !== col.key
        ? { key: col.key, dir: "desc" }
        : { key: col.key, dir: prev.dir === "desc" ? "asc" : "desc" }
    );
  };

  const batterPointDistsIndex = useMemo(() => {
    const pd =
      batterPointDistsRaw && typeof batterPointDistsRaw === "object"
        ? batterPointDistsRaw
        : null;
    if (!pd) return null;

    const pickIdx = (obj) => {
      const out = {};
      if (!obj || typeof obj !== "object") return out;
      for (const [name, bins] of Object.entries(obj)) {
        const k = keynorm(name);
        if (k) out[k] = bins;
      }
      return out;
    };

    return {
      dk: pickIdx(pd.dk ?? pd.DK),
      fd: pickIdx(pd.fd ?? pd.FD),
    };
  }, [batterPointDistsRaw]);

  const pitcherPointDistsIndex = useMemo(() => {
    const pd =
      pitcherPointDistsRaw && typeof pitcherPointDistsRaw === "object"
        ? pitcherPointDistsRaw
        : null;
    if (!pd) return null;

    const pickIdx = (obj) => {
      const out = {};
      if (!obj || typeof obj !== "object") return out;
      for (const [name, bins] of Object.entries(obj)) {
        const k = keynorm(name);
        if (k) out[k] = bins;
      }
      return out;
    };

    return {
      dk: pickIdx(pd.dk ?? pd.DK),
      fd: pickIdx(pd.fd ?? pd.FD),
    };
  }, [pitcherPointDistsRaw]);

  const openPointDist = (row, which) => {
    const name = String(row?.player || "").trim();
    const key = keynorm(name);
    setPointDistSite(which);
    setPointDistPlayerKey(key);
    setPointDistPlayerName(name);
    setPointDistIsBatter(row?.type === "BAT");
    setPointDistOpen(true);
  };

  const closePointDist = () => setPointDistOpen(false);

  const activeRowForModal = useMemo(() => {
    if (!pointDistPlayerKey) return null;
    return sorted.find((x) => keynorm(x?.player) === pointDistPlayerKey) ?? null;
  }, [sorted, pointDistPlayerKey]);

  const activeBinsForModal = useMemo(() => {
    if (!pointDistPlayerKey) return null;

    const siteKey = pointDistSite === "DK" ? "dk" : "fd";
    const idx = pointDistIsBatter ? batterPointDistsIndex : pitcherPointDistsIndex;

    return idx?.[siteKey]?.[pointDistPlayerKey] ?? null;
  }, [
    pointDistPlayerKey,
    pointDistSite,
    pointDistIsBatter,
    batterPointDistsIndex,
    pitcherPointDistsIndex,
  ]);

  const tableRef = useRef(null);
  const [playerColWidth, setPlayerColWidth] = useState(240);

  useEffect(() => {
    if (!sorted.length) return;

    let cancelled = false;

    const measure = async () => {
      try {
        await document.fonts?.ready;
      } catch (_) {}

      const font = tableRef.current
        ? getComputedStyle(tableRef.current).font
        : getComputedStyle(document.body).font;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.font = font;

      let maxText = 0;
      for (const r of sorted) {
        const name = String(r.player || "");
        const w = ctx.measureText(name).width;
        if (w > maxText) maxText = w;
      }

      const next = Math.round(Math.max(170, Math.min(300, maxText + 40)));
      if (!cancelled) setPlayerColWidth(next);
    };

    const id = requestAnimationFrame(() => {
      measure();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [sorted, theme]);

  const stats = useMemo(() => computeStats(sorted, columns), [sorted, columns]);
  const updatedText = useLastUpdatedFromSources(BATTERS_SOURCE, PITCHERS_SOURCE);

  const textSz = "text-[12px] md:text-[12px]";
  const headerCellBase =
    "px-1.5 py-1 font-semibold text-center whitespace-nowrap cursor-pointer select-none";
  const cellClsBase = "px-1.5 py-0.5 text-center whitespace-nowrap";

  const renderCell = (key, r) => {
    switch (key) {
      case "type":
        return r.type || "";
      case "pos":
        return getPosForSite(r, site) || "";
      case "player":
        return r.player || "";
      case "team":
      case "opp":
        return r[key] || "";

      case "dk_sal":
        return fmtMoney0(getRenderedSalary(siteIdIndex, r, "dk", slate, r.dk_sal));

      case "fd_sal":
        return fmtMoney0(getRenderedSalary(siteIdIndex, r, "fd", slate, r.fd_sal));

      case "dk_pown":
      case "fd_pown":
      case "dk_optimal":
      case "fd_optimal":
        return fmtPct1(r[key]);

      default:
        return typeof r[key] === "number" ? fmt1(r[key]) : r[key] ?? "";
    }
  };

  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm overflow-hidden"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden";

  const headerBgClass = isDark
    ? "bg-slate-900 text-slate-200"
    : "bg-gray-50 text-gray-800";

  return (
    <div className={outerClass}>
      <div className="w-full px-4 md:px-6 py-5">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">MLB — Projections</h1>
            <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {updatedText || "—"}</span>
          </div>
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark
                  ? "bg-slate-900/90 ring-slate-700/80"
                  : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
        </header>
        <section aria-label="Site and slate" className={cardClass + " mb-4"}>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3">
            <div
              className={
                "inline-flex flex-wrap items-center gap-1 border-b " +
                (isDark
                  ? "border-slate-800"
                  : "border-slate-200")
              }
            >
              {["dk", "fd", "both"].map((k) => (
                <button
                  key={k}
                  onClick={() => setSite(k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                    site === k
                      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                      : isDark
                      ? "border-transparent text-slate-400 hover:text-white"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                  title={`Show ${SITES[k].label}`}
                >
                  {k !== "both" ? (
                    <img
                      src={SITES[k].logo}
                      alt={SITES[k].label}
                      className="w-4 h-4"
                    />
                  ) : null}
                  <span>{SITES[k].label}</span>
                </button>
              ))}
            </div>

            <div
              className={
                "inline-flex flex-wrap items-center gap-1 border-b " +
                (isDark
                  ? "border-slate-800"
                  : "border-slate-200")
              }
              title="Slate filter"
            >
              {[
                { k: "all", label: "All" },
                { k: "early", label: "Early" },
                { k: "main", label: "Main" },
                { k: "late", label: "Late" },
              ].map((x) => (
                <button
                  key={x.k}
                  onClick={() => setSlate(x.k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                    slate === x.k
                      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                      : isDark
                      ? "border-transparent text-slate-400 hover:text-white"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {x.label}
                </button>
              ))}
            </div>

            <div
              className={
                "inline-flex flex-wrap items-center gap-1 border-b " +
                (isDark
                  ? "border-slate-800"
                  : "border-slate-200")
              }
              title="Batters / Pitchers filter"
            >
              {[
                { k: "bat", label: "Batters" },
                { k: "p", label: "Pitchers" },
                { k: "both", label: "Both" },
              ].map((x) => (
                <button
                  key={x.k}
                  onClick={() => setKind(x.k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                    kind === x.k
                      ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                      : isDark
                      ? "border-transparent text-slate-400 hover:text-white"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {x.label}
                </button>
              ))}
            </div>

          </div>
        </section>

        {err && (
          <div className={"mb-2 font-semibold " + (isDark ? "text-red-400" : "text-red-600")}>
            Data load error: {err}
          </div>
        )}

        <section aria-label="Player projections" className={cardClass}>
          <div className={"flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <h2 className="text-sm font-semibold">Player Projections</h2>
            <button
              className={
                "h-8 px-3 rounded-md text-xs font-semibold transition " +
                (isDark
                  ? "bg-sky-500 text-slate-950 hover:bg-sky-400 shadow-sm"
                  : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm")
              }
              onClick={() =>
                downloadCSV(sorted, columns, site, slate, siteIdIndex)
              }
            >
              Export CSV
            </button>

          </div>
          <div className="px-4 pt-2">
            <div className="flex flex-wrap items-center gap-1 border-b border-slate-700/30">
              {posOptions.map((p) => {
                const active = posSel.includes(p);
                return (
                  <button
                    key={p}
                    onClick={() =>
                      setPosSel((prev) =>
                        prev.includes(p)
                          ? prev.filter((x) => x !== p)
                          : [...prev, p]
                      )
                    }
                    className={`h-9 px-2.5 border-b-2 text-xs font-semibold transition ${
                      active
                        ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                        : isDark
                        ? "border-transparent text-slate-400 hover:text-white"
                        : "border-transparent text-slate-500 hover:text-slate-900"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>

          </div>
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="ml-1">
              <TeamsDropdown
                allTeams={allTeams}
                selected={teamsSel}
                onChange={setTeamsSel}
                isDark={isDark}
              />
            </div>

            <input
              className={
                "h-8 w-full sm:ml-auto sm:w-64 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400")
              }
              placeholder="Search player / team / opp…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />

          </div>
          <div className="overflow-x-auto">
            <div className="max-h-[70vh] overflow-y-auto">
              <table
                ref={tableRef}
                className={`border-separate table-auto ${textSz}`}
                style={{ borderSpacing: 0, width: "max-content", minWidth: "100%" }}
              >
                <thead className={headerBgClass + " sticky top-0 z-40"}>
                  <tr>
                    {columns.map((c) => {
                      const thStyle = c.sticky
                        ? {
                            left: 0,
                            minWidth: playerColWidth,
                            width: playerColWidth,
                            maxWidth: playerColWidth,
                            backgroundColor: isDark ? "#0f172a" : "#f9fafb",
                          }
                        : undefined;

                      return (
                        <th
                          key={c.key}
                          className={`${headerCellBase} ${c.sticky ? "sticky z-50" : ""}`}
                          style={thStyle}
                          onClick={() => onSort(c)}
                          title="Click to sort"
                        >
                          <div className="inline-flex items-center justify-center gap-1 w-full">
                            <span>{c.label}</span>
                            <span className={isDark ? "text-slate-400" : "text-gray-400"}>
                              {sort.key === c.key ? (sort.dir === "desc" ? "▼" : "▲") : "▲"}
                            </span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td
                        className={
                          cellClsBase +
                          " text-center " +
                          (isDark ? "text-slate-400" : "text-gray-500")
                        }
                        colSpan={columns.length}
                      >
                        Loading…
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    sorted.map((r, i) => {
                      const rowBg =
                        i % 2 === 0
                          ? isDark
                            ? "bg-slate-950"
                            : "bg-white"
                          : isDark
                          ? "bg-slate-900"
                          : "bg-gray-50";

                      const cellCls =
                        cellClsBase + " " + (isDark ? "text-slate-100" : "text-gray-900");

                      return (
                        <tr
                          key={`${r.type}-${r.player}-${i}`}
                          className={
                            rowBg +
                            " " +
                            (isDark
                              ? "hover:bg-slate-900/80 transition-colors"
                              : "hover:bg-blue-50 transition-colors")
                          }
                        >
                          {columns.map((c) => {
                            let value = c.key === "pos" ? getPosForSite(r, site) : r[c.key];

                            if (c.key === "dk_sal") {
                              value = getRenderedSalary(siteIdIndex, r, "dk", slate, r.dk_sal);
                            }
                            if (c.key === "fd_sal") {
                              value = getRenderedSalary(siteIdIndex, r, "fd", slate, r.fd_sal);
                            }

                            const textColor =
                              c.key !== "pos"
                                ? colorFor(value, c.key, stats, isDark)
                                : null;

                            const stickyCls = c.sticky
                              ? `px-2 py-1 sticky z-30 ${rowBg} shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.35)] ${
                                  isDark ? "text-slate-100" : "text-gray-900"
                                } text-left`
                              : cellCls;

                            const tdStyle = c.sticky
                              ? {
                                  left: 0,
                                  minWidth: playerColWidth,
                                  width: playerColWidth,
                                  maxWidth: playerColWidth,
                                }
                              : undefined;

                            const isProjCell = c.key === "dk_proj" || c.key === "fd_proj";
                            const projSite = c.key === "dk_proj" ? "DK" : "FD";

                            return (
                              <td
                                key={c.key}
                                className={stickyCls}
                                style={tdStyle}
                                title={String(value ?? "")}
                              >
                                {c.key === "player" ? (
                                  <div className="flex items-center justify-start gap-2">
                                    <span className="w-5 shrink-0 flex items-center justify-center">
                                      <img
                                        src={teamLogo(r.team)}
                                        alt=""
                                        className="w-4 h-4 rounded-sm object-contain"
                                        onError={(e) => {
                                          e.currentTarget.style.visibility = "hidden";
                                        }}
                                      />
                                    </span>
                                    <span className="whitespace-nowrap">
                                      {renderCell(c.key, r)}
                                    </span>
                                  </div>
                                ) : isProjCell ? (
                                  <button
                                    className={[
                                      "group relative w-full text-center font-semibold rounded-md px-1 py-0.5",
                                      "underline underline-offset-4 decoration-sky-400/70",
                                      "hover:decoration-sky-300 hover:bg-sky-500/10",
                                      "transition cursor-pointer",
                                    ].join(" ")}
                                    style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                                    onClick={() => openPointDist(r, projSite)}
                                    title="Click to open point distribution chart"
                                  >
                                    {renderCell(c.key, r)}
                                  </button>
                                ) : (
                                  <span
                                    style={textColor ? { color: textColor, fontWeight: 600 } : undefined}
                                  >
                                    {renderCell(c.key, r)}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}

                  {!loading && !sorted.length && (
                    <tr>
                      <td
                        className={
                          cellClsBase +
                          " text-center " +
                          (isDark ? "text-slate-500" : "text-gray-500")
                        }
                        colSpan={columns.length}
                      >
                        No data.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <div
          className={
            "mt-2 text-[12px] flex flex-wrap gap-3 " +
            (isDark ? "text-slate-500" : "text-gray-500")
          }
        >
          <span>pOWN% = Projected Ownership</span>
          <span>Opt% = Optimal%</span>
          <span>Type = BAT (batter) / P (pitcher)</span>
          <span>Click DK/FD Proj for point distributions</span>
          <span>Slate filter now works on DK, FD, and Both</span>
        </div>
      </div>

      <PointDistModal
        open={pointDistOpen}
        onClose={closePointDist}
        isDark={isDark}
        site={pointDistSite}
        playerName={pointDistPlayerName}
        isBatter={pointDistIsBatter}
        proj={
          pointDistSite === "DK"
            ? num(activeRowForModal?.dk_proj)
            : num(activeRowForModal?.fd_proj)
        }
        bins={activeBinsForModal}
      />
    </div>
  );
}